import CircularProgress from '@mui/material/CircularProgress';
import _ from 'lodash';
import React__default, { forwardRef, useRef, useState, useEffect } from 'react';
import { SearchField, Label, Input, FieldError } from 'react-aria-components';
import { SearchIcon } from '../icons/SearchIcon.js';
import { WarningIcon } from '../icons/WarningIcon.js';
import { Button } from './Button.js';
import './TextInput.scss';

/* eslint-disable no-param-reassign -- input ref mutations are required for controlled input behavior */
const TextInput = forwardRef(({ variant = 'outline', type: inputType = 'text', rounding = '10', size = 'large', name, mask: inputMask, maskPlaceholderChar = '_', maskWildcardChar = '*', maskAllowedChars, className, ariaLabel, label, placeholder, hasError, isDisabled, isReadOnly, isButton, maxLength, isRequired, isOptional, isClearable, isLoading, ...props }, ref) => {
    // Set default mask for certain types
    let type = inputType;
    let mask = inputMask;
    if (inputType === 'price') {
        type = 'text';
        if (!inputMask) {
            mask = '$*';
            maskWildcardChar = '*';
            maskAllowedChars = /[\d|,]/;
        }
    }
    else if (inputType === 'price-cents') {
        type = 'text';
        if (!inputMask) {
            mask = '$*';
            maskWildcardChar = '*';
            maskAllowedChars = /[\d|\.|,]/;
        }
    }
    else if (inputType === 'tel') {
        if (!inputMask) {
            mask = '+_ (___) ___-____';
            maskPlaceholderChar = '_';
            maskAllowedChars = /\d/;
        }
    }
    else if (inputType === 'zipcode') {
        type = 'text';
        if (!inputMask) {
            mask = '_____';
            maskPlaceholderChar = '_';
            maskAllowedChars = /\d/;
        }
    }
    else if (inputType === 'zipcode-ext') {
        type = 'text';
        if (!inputMask) {
            mask = '_____-____';
            maskPlaceholderChar = '_';
            maskAllowedChars = /\d/;
        }
    }
    else if (inputType === 'percentage') {
        type = 'text';
        if (!inputMask) {
            mask = '*%';
            maskWildcardChar = '*';
            maskAllowedChars = /\d/;
        }
    }
    //
    const internalRef = useRef(null);
    // Merge internal and forwarded refs so both work
    React__default.useImperativeHandle(ref, () => internalRef.current);
    const [internalValue, setInternalValue] = useState(props.value ?? props.defaultValue ?? '');
    const displayValue = mask
        ? applyMask(internalValue, mask, maskPlaceholderChar, maskWildcardChar, maskAllowedChars)
        : internalValue;
    // Sync internal state if value prop changes
    useEffect(() => {
        if (props.value !== undefined) {
            setInternalValue(props.value);
            handleBlur(props.value, undefined, setInternalValue);
        }
    }, [props.value]);
    const handleChange = (newValue, source) => {
        if (isDisabled || isReadOnly) {
            return;
        }
        const adjustedValue = handleInputDelete(internalValue, newValue, mask, maskPlaceholderChar, maskWildcardChar, maskAllowedChars);
        // Strip mask to get raw value
        let rawValue = stripMask(adjustedValue, mask, maskPlaceholderChar);
        // For phone numbers with the default mask, limit to 11 digits (1 country code + 10 US digits)
        if (inputType === 'tel' && mask === '+_ (___) ___-____' && rawValue.length > 11) {
            rawValue = rawValue.slice(0, 11);
        }
        // For percentage, limit to 0-100
        if (inputType === 'percentage') {
            const numValue = parseFloat(rawValue);
            if (!isNaN(numValue)) {
                // Clamp to 0-100 range
                if (numValue < 0) {
                    rawValue = '0';
                }
                else if (numValue > 100) {
                    rawValue = '100';
                }
            }
        }
        // Store raw value (internalValue stores raw, not masked)
        setInternalValue(rawValue);
        props.onChange?.(rawValue, source ?? 'USER');
    };
    // Autofill
    const handleAutofillAnimation = (e) => {
        if (e.animationName === 'hasAutofill') {
            // Autofill style applied — value may or may not be here yet
            requestAnimationFrame(() => {
                checkAutofill();
            });
        }
    };
    const checkAutofill = () => {
        const val = internalRef.current?.value ?? '';
        if (val) {
            handleChange(val, 'AUTOFILL');
        }
    };
    // Fallback: listen for browser input events too
    const handleNativeInput = (e) => {
        const val = e.currentTarget.value;
        if (val) {
            handleChange(val, 'USER'); // will fire on typing
        }
    };
    const handleBlur = (currentValue, callbackBlurFn, callbackSetValFn) => {
        // Special case: price formatting
        if (inputType === 'price' || inputType === 'price-cents') {
            // Remove sign and commas, keep decimal to track cents
            let rawValue = stripMask(currentValue, '$*,', maskPlaceholderChar);
            callbackBlurFn?.(rawValue);
            const price = parseFloat(stripMask(rawValue, mask, maskPlaceholderChar));
            if (isNaN(price)) {
                rawValue = '';
            }
            else {
                rawValue = price.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: inputType === 'price-cents' ? 2 : 0,
                    maximumFractionDigits: inputType === 'price-cents' ? 2 : 0,
                });
            }
            callbackSetValFn?.(rawValue);
        }
        else if (inputType === 'percentage') {
            // Special case: percentage formatting - clamp to 0-100 and format with %
            let rawValue = stripMask(currentValue, mask, maskPlaceholderChar);
            const numValue = parseFloat(rawValue);
            if (isNaN(numValue) || rawValue === '') {
                rawValue = '';
                callbackBlurFn?.('');
            }
            else {
                // Clamp to 0-100 range
                const clampedValue = Math.max(0, Math.min(100, numValue));
                rawValue = clampedValue.toString();
                callbackBlurFn?.(rawValue);
                // Format with % symbol
                rawValue = `${rawValue}%`;
            }
            callbackSetValFn?.(rawValue);
        }
        else {
            callbackBlurFn?.(stripMask(currentValue, mask, maskPlaceholderChar));
        }
    };
    const showClearButton = !props.rightContent &&
        isClearable &&
        internalValue?.trim() &&
        !isDisabled &&
        !isReadOnly &&
        !isLoading;
    // Auto-add search icon if type is 'search' and no leftContent provided
    if (type === 'search' && !props.leftContent) {
        props.leftContent = React__default.createElement(SearchIcon, null);
    }
    return (React__default.createElement(SearchField, { "data-rev-component": 'text-input', "data-rev-isloading": isLoading, type: type, name: name, className: className, "aria-label": ariaLabel, isDisabled: isDisabled, isInvalid: !!hasError, isReadOnly: isReadOnly, isRequired: isRequired, value: displayValue, defaultValue: props.defaultValue, onChange: handleChange, onKeyDown: (e) => {
            props.onKeyChange?.(e, 'DOWN');
        }, onKeyUp: (e) => {
            props.onKeyChange?.(e, 'UP');
        } },
        label && (React__default.createElement(Label, { className: 'text-input-label--rev', "data-invalid": !!hasError, "data-rev-is-old-style": variant === 'old-style' },
            label,
            isRequired && (React__default.createElement("span", { className: 'field-label-required--rev' }, "(required)")),
            isOptional && (React__default.createElement("span", { className: 'field-label-optional--rev' }, "(optional)")))),
        React__default.createElement("div", { className: 'text-input-group--rev', "data-rev-variant": variant, "data-rev-size": size, "data-rev-isloading": isLoading, "data-rev-rounding": rounding, onClick: () => internalRef.current?.focus(), "data-disabled": isDisabled || isReadOnly, "data-rev-is-button": isButton, "data-invalid": !!hasError },
            !!props.leftContent && (React__default.createElement("div", { className: 'text-input-side-content-left--rev' }, props.leftContent)),
            React__default.createElement(Input, { ref: internalRef, className: 'text-input-input--rev', autoComplete: props?.autocomplete, "aria-invalid": !!hasError, placeholder: placeholder, maxLength: maxLength, onAnimationStart: handleAutofillAnimation, onInput: handleNativeInput, onFocus: () => {
                    props.onFocus?.(internalValue);
                }, onBlur: () => {
                    handleBlur(internalValue, props.onBlur, setInternalValue);
                }, disabled: isDisabled || isReadOnly }),
            showClearButton && (React__default.createElement(Button, { slot: 'clear', className: 'text-input-side-button--rev', "aria-label": 'Clear', variant: 'ghost', rounding: '8', icon: React__default.createElement(XIcon, null), size: 'small', onPress: () => {
                    handleChange('', 'USER');
                    props.onClear?.();
                } })),
            isLoading && (React__default.createElement("div", { className: 'text-input-side-button--rev spinner--rev' },
                React__default.createElement(CircularProgress, { color: 'inherit' }))),
            !!props.rightContent &&
                !isLoading && ( // don't show custom content if loading
            React__default.createElement("div", { className: 'text-input-side-content-right--rev' }, props.rightContent))),
        maxLength != null && (React__default.createElement("div", { className: 'text-input-char-count--rev' },
            internalValue.length,
            "/",
            maxLength)),
        hasError && typeof hasError === 'string' && (React__default.createElement(FieldError, { className: 'text-input-error--rev' },
            React__default.createElement(WarningIcon, null),
            hasError))));
});
TextInput.displayName = 'TextInput';
function applyMask(rawValue, mask, maskPlaceholderChar, maskWildcardChar, maskAllowedChars) {
    let outputValue = '';
    // Ensure rawValue is a string
    const stringValue = String(rawValue ?? '');
    const remainingValueChars = stripMask(stringValue, mask, maskPlaceholderChar).split('');
    const getEndOfMask = () => {
        if (!mask) {
            return '';
        }
        let end = '';
        for (let i = mask.length - 1; i >= 0; i--) {
            const mc = mask[i];
            if (mc === maskPlaceholderChar || mc === maskWildcardChar) {
                break;
            }
            else {
                end = mc + end;
            }
        }
        return end;
    };
    for (let i = 0; i < mask.length; i++) {
        const mc = mask[i];
        if (remainingValueChars.length === 0) {
            break;
        }
        const addChar = (ch) => {
            if (ch !== undefined) {
                if (maskAllowedChars) {
                    // Only add if allowed
                    if (maskAllowedChars.test(ch)) {
                        outputValue += ch;
                    }
                }
                else {
                    // No restrictions
                    outputValue += ch;
                }
            }
        };
        if (mc === maskWildcardChar) {
            // Consumes all remaining characters
            while (remainingValueChars.length > 0) {
                addChar(remainingValueChars.shift());
            }
            // Add the rest of the mask after the wildcard
            const endOfMask = getEndOfMask();
            outputValue += endOfMask;
        }
        else if (mc === maskPlaceholderChar) {
            addChar(remainingValueChars.shift());
        }
        else {
            outputValue += mc;
        }
    }
    return outputValue;
}
function handleInputDelete(oldValue, newValue, mask, maskPlaceholderChar, maskWildcardChar, maskAllowedChars) {
    // Ensure values are strings
    const stringNewValue = String(newValue ?? '');
    const stringOldValue = String(oldValue ?? '');
    if (stringNewValue.trim() === '') {
        return '';
    }
    function longestCommonPrefix(a, b) {
        let i = 0;
        const len = Math.min(a.length, b.length);
        while (i < len && a[i] === b[i]) {
            i++;
        }
        return a.slice(0, i);
    }
    if (mask) {
        // Detect if character(s) were deleted from the end
        const oldDisplayValue = applyMask(stripMask(stringOldValue, mask, maskPlaceholderChar), mask, maskPlaceholderChar, maskWildcardChar, maskAllowedChars);
        const commonPrefix = longestCommonPrefix(oldDisplayValue, stringNewValue);
        if (commonPrefix.length === stringNewValue.length &&
            oldDisplayValue.length > stringNewValue.length) {
            // Characters were deleted from the end, delete that many characters from before the ending mask instead
            const charDiff = oldDisplayValue.length - stringNewValue.length;
            // Remove diff from newValue
            let adjustedValue = stripMask(oldDisplayValue, mask, maskPlaceholderChar);
            adjustedValue = adjustedValue.slice(0, adjustedValue.length - charDiff);
            return adjustedValue;
        }
        else {
            return stringNewValue;
        }
    }
    else {
        return stringNewValue;
    }
}
const stripMask = (value, mask, maskPlaceholderChar) => {
    // Ensure value is always a string
    const stringValue = String(value ?? '');
    if (!mask || !maskPlaceholderChar) {
        return stringValue;
    }
    // Collect all unique characters from the mask that are not placeholders or wildcards
    // These are the characters we need to remove from the value
    const maskChars = [];
    for (let i = 0; i < mask.length; i++) {
        const char = mask[i];
        if (char !== maskPlaceholderChar && char !== '*' && !maskChars.includes(char)) {
            maskChars.push(char);
        }
    }
    function removeMatches(str, matches) {
        if (!str || typeof str !== 'string') {
            return '';
        }
        let result = str;
        for (const match of matches) {
            if (match) {
                result = result.replace(new RegExp(_.escapeRegExp(match), 'g'), '');
            }
        }
        return result;
    }
    return removeMatches(stringValue, maskChars);
};
// -- Icons --
// SearchIcon and WarningIcon are now imported from '../icons/'
const XIcon = () => {
    return (React__default.createElement("svg", { className: 'text-input-x-icon--rev', xmlns: 'http://www.w3.org/2000/svg', width: '24', height: '24', viewBox: '0 0 24 24', fill: 'none' },
        React__default.createElement("path", { d: 'M8 16L16 8M8 8L16 16', stroke: 'black', strokeOpacity: '0.6', strokeWidth: '1.5', strokeLinecap: 'round', strokeLinejoin: 'round' })));
};
TextInput.displayName = 'TextInput';

export { TextInput, XIcon };
//# sourceMappingURL=TextInput.js.map
